import {AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {
  AgentAction,
  AgentAttachmentKind,
  AgentAttachmentProcessingStage,
  AgentAttachmentResult,
  AgentAttachmentStatus,
  AgentAttachmentUiState,
  AgentChange,
  AgentChat,
  AgentContent,
  AgentEventType,
  AgentField,
  AgentLoopMode,
  AgentOperation,
  AgentOperationKind,
  AgentOperationResult,
  AgentOperationStatus,
  AgentQuestion,
  AgentTraceStep,
  ViewMode
} from './domain/ai-agent';
import {AiAgentService} from './service/ai-agent.service';
import {NavigationEnd, Router} from '@angular/router';
import {SidenavService} from '../../service/sidenav.service';
import {ApiService} from '../../../infrastructure/service/api.service';
import {HttpClient} from '@angular/common/http';
import {SnackbarService} from '../../../infrastructure/service/snackbar.service';
import {TranslateService} from '@ngx-translate/core';
import {filter, Subscription} from 'rxjs';
import {WebSocketHandler} from '../../../infrastructure/service/web-socket-handler';
import {WebSocketService, WebSocketServiceInstance} from '../../../infrastructure/service/web-socket.service';
import {WSMessage} from '../../../infrastructure/domain/ws-message';
import { AiAgentAvatarState } from './ai-agent-avatar.component';

type GuidedFieldStatus = 'available' | 'filled' | 'unavailable' | 'target_not_found' | 'error';
type AgentPendingState = 'thinking' | 'executing';
type LoopVisualState = 'final_answer' | 'collect_data' | 'executing' | 'awaiting_confirmation' | 'success' | 'partial_success' | 'error';
type AgentPlanItemStatus = 'pending' | 'running' | 'success' | 'error';
type ComposerAttachmentState = 'pending' | 'rejected';

interface GuidedFieldView {
  field: AgentField;
  targetId?: string;
  status: GuidedFieldStatus;
  detail: string;
}

interface GuidedNavigationView {
  targetId: string;
  title: string;
  value: string;
  description: string;
}

interface AgentPlanItem {
  operation: AgentOperation;
  result?: AgentOperationResult;
  status: AgentPlanItemStatus;
}

interface ComposerAttachment {
  id: string;
  file: File;
  fileName: string;
  contentType: string;
  size: number;
  kind: AgentAttachmentKind;
  state: ComposerAttachmentState;
  failureCode?: string;
  message?: string;
}

@Component({
    selector: 'app-ai-agent',
    templateUrl: './ai-agent.component.html',
    styleUrls: ['./ai-agent.component.scss'],
    standalone: false
})
export class AiAgentComponent extends ApiService implements OnInit, AfterViewInit, OnDestroy, WebSocketHandler {
  private static readonly GUIDED_FIELD_REFRESH_MAX_RETRIES = 15;
  private static readonly GUIDED_OVERLAY_CLASS = 'guided-tour-overlay';
  private static readonly GUIDED_HIGHLIGHT_CLASS = 'guided-tour-highlight';
  private static readonly EXPLICIT_CONFIRMATION_PROMPT = 'Faça para mim';
  private static readonly CHAT_TITLE_MAX_LENGTH = 80;
  private static readonly ATTACHMENT_MAX_SIZE_BYTES = 2 * 1024 * 1024;

  private static readonly GUIDED_TARGETS: Record<string, Record<string, string>> = {
    'app/modules/task/task-edit/task-edit.component.html': {
      name: 'task-name',
      description: 'task-description',
      owner: 'task-owner',
      priority: 'task-priority',
      status: 'task-status',
      type: 'task-type',
      project: 'task-project',
      dueDate: 'task-due-date',
      startTime: 'task-start-time',
      endTime: 'task-end-time',
      labels: 'task-labels',
      subtasks: 'task-subtasks',
      relatedTasks: 'task-related-tasks',
      attachments: 'task-attachments',
      users: 'task-followers'
    },
    'app/modules/project/project-edit/project-edit.component.html': {
      name: 'project-name',
      description: 'project-description',
      owner: 'project-owner',
      status: 'project-status',
      type: 'project-type',
      labels: 'project-labels',
      customer: 'project-customer',
      processNumber: 'project-process-number',
      legalAmount: 'project-legal-amount',
      users: 'project-followers'
    },
    'app/modules/finance/finance-edit/finance-edit.component.html': {
      customer: 'finance-customer',
      vendor: 'finance-vendor',
      type: 'finance-type',
      description: 'finance-description',
      name: 'finance-name',
      dueDate: 'finance-due-date',
      paidDate: 'finance-paid-date',
      amount: 'finance-amount',
      labels: 'finance-labels'
    },
    'app/modules/customer/customer-edit/customer-edit.component.html': {
      name: 'customer-name',
      taxId: 'customer-tax-id',
      legalName: 'customer-legal-name'
    }
  };

  public viewMode: typeof ViewMode = ViewMode;
  public chats: AgentChat[] = [];
  public chat!: AgentChat | undefined;
  public viewChat = false;
  public agentContent!: AgentContent;
  public chatQuestions: AgentQuestion[] = [];
  public actualStep = 0;
  public question = '';
  public draftAttachments: ComposerAttachment[] = [];
  public editingChatTitle = '';
  public guidedNavigation?: GuidedNavigationView;
  public guidedFields: GuidedFieldView[] = [];
  public activeGuidedFieldIndex = -1;
  public isEditingChatTitle = false;
  public isSavingChatTitle = false;
  public readonly chatTitleMaxLength = AiAgentComponent.CHAT_TITLE_MAX_LENGTH;

  @ViewChild('aiInput') aiInput!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('attachmentInput') attachmentInput?: ElementRef<HTMLInputElement>;
  @ViewChild('chatTitleInput') chatTitleInput?: ElementRef<HTMLInputElement>;
  @ViewChild('chat') private scrollContainer!: ElementRef<HTMLDivElement>;

  public view: ViewMode = ViewMode.FAB;
  public isTyping = false;
  public isDrawerOpened = false;
  public isMenuExpanded = false;
  public pendingState: AgentPendingState = 'thinking';
  public pendingQuestionIndex = -1;
  public isAttachmentDragActive = false;
  public isMobileDrawerOpened = false;

  private drawerOpenedSubscription?: Subscription;
  private routeSubscription?: Subscription;
  private menuExpandedSubscription?: Subscription;
  private websocketInstance?: WebSocketServiceInstance;
  private guidedRefreshTimeout?: ReturnType<typeof setTimeout>;
  private guidedOverlaySyncTimeout?: ReturnType<typeof setTimeout>;
  private pendingGuidedAdvanceFieldId?: string;
  private pendingClientRequestId?: string;
  private composerAttachmentSequence = 0;
  private pendingDraftRestore?: { question: string; attachments: ComposerAttachment[] };
  private dismissedConfirmationKeys = new Set<string>();
  private loopPlanItemsCache = new WeakMap<AgentContent, AgentPlanItem[]>();

  constructor(private service: AiAgentService,
              private router: Router,
              private sidenavService: SidenavService,
              private snackbar: SnackbarService,
              private translate: TranslateService,
              private websocket: WebSocketService,
              http: HttpClient) {
    super(http);
  }

  ngOnInit(): void {
    this.drawerOpenedSubscription = this.sidenavService.drawerOpened$.subscribe((opened) => {
      this.isDrawerOpened = opened;
      this.isMobileDrawerOpened = opened && this.isMobileViewport();

      if (this.isMobileDrawerOpened) {
        this.close();
      }
    });

    this.menuExpandedSubscription = this.sidenavService.menuExpanded$.subscribe((expanded) => {
      this.isMenuExpanded = expanded;
    });

    this.routeSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        if (this.actualStep > 0) {
          this.scheduleGuidedFieldRefresh();
        }
      });

    this.websocketInstance = this.websocket.connect('/ai-agent', this);
  }

  ngAfterViewInit(): void {
    this.adjustTextareaHeight();
  }

  ngOnDestroy(): void {
    this.drawerOpenedSubscription?.unsubscribe();
    this.routeSubscription?.unsubscribe();
    this.menuExpandedSubscription?.unsubscribe();
    this.websocketInstance?.disconnect();
    if (this.guidedRefreshTimeout) {
      clearTimeout(this.guidedRefreshTimeout);
    }
    if (this.guidedOverlaySyncTimeout) {
      clearTimeout(this.guidedOverlaySyncTimeout);
    }
    this.clearGuidedHighlight();
  }

  @HostListener('input', ['$event.target'])
  onTextareaInput(textarea: HTMLTextAreaElement): void {
    this.adjustTextareaHeight();
  }

  adjustTextareaHeight(): void {
    if (this.aiInput?.nativeElement) {
      const textarea = this.aiInput.nativeElement;
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  }

  openInput(): void {
    setTimeout(() => {
      if (this.aiInput?.nativeElement) {
        this.aiInput.nativeElement.focus();
        this.adjustTextareaHeight();
      }
      this.view = ViewMode.CHAT;
    }, 100);
  }

  onInputBlur(): void {
    if (!this.question && this.chatQuestions.length === 0 && this.draftAttachments.length === 0) {
      this.close();
    }
  }

  sendQuestion(): void {
    const sendableAttachments = this.getSendableAttachments();
    const normalizedQuestion = this.question?.trim();

    if (!normalizedQuestion && sendableAttachments.length === 0) {
      return;
    }

    const questionToSend = normalizedQuestion || this.translate.instant('ai.agent.attachments.default_question');
    this.pendingDraftRestore = {
      question: this.question,
      attachments: sendableAttachments.map((attachment) => ({...attachment}))
    };

    this.consumeSubmittedAttachments(sendableAttachments.map((attachment) => attachment.id));
    this.resetComposerQuestion();
    this.ask(questionToSend, 'thinking', sendableAttachments);
  }

  ask(question: string, pendingState: AgentPendingState = 'thinking', attachments: ComposerAttachment[] = []): void {
    this.exitStep();

    const clientRequestId = this.createClientRequestId();
    const item: AgentQuestion = {
      question,
      context: {},
      attachmentResults: attachments.map((attachment) => this.mapComposerAttachmentToResult(attachment, 'sending'))
    };
    const idx = this.chatQuestions.length;
    this.chatQuestions.push(item);
    this.isTyping = true;
    this.pendingQuestionIndex = idx;
    this.pendingState = pendingState;
    this.pendingClientRequestId = clientRequestId;

    this.service.ask(question, this.chat?.id, clientRequestId, attachments.map((attachment) => attachment.file)).then((response: AgentQuestion) => {
      if (!this.chat?.id && response.chat?.id) {
        this.service.findChatById(response.chat.id).then(chat => this.chat = chat);
      }
      this.pendingDraftRestore = undefined;
      this.handleAIAgentResponse(response, idx);
    }).catch(() => {
      this.restoreFailedDraft();
      this.handleAIAgentResponse({
        question,
        context: {},
        content: {
          mode: AgentLoopMode.ERROR,
          message: this.translate.instant('ai.agent.error.generic'),
          error: 'request_failed',
          operations: []
        },
        attachmentResults: attachments.map((attachment) => ({
          ...this.mapComposerAttachmentToResult(attachment, 'failed'),
          status: AgentAttachmentStatus.FAILED,
          stage: AgentAttachmentProcessingStage.VALIDATION,
          failureCode: 'request_failed',
          message: this.translate.instant('ai.agent.error.generic')
        }))
      }, idx);
    });

    setTimeout(() => {
      if (!this.isNearBottom()) {
        this.scrollToBottom();
      }
    });
  }

  handleMessage(message: WSMessage): void {
    const normalizedContent = this.normalizeAgentContent(message?.object || {});
    const pendingIndex = this.resolvePendingLoopIndex(normalizedContent);
    const previousContent = pendingIndex >= 0 ? this.chatQuestions[pendingIndex]?.content : undefined;
    const content = this.mergeGuidedActions(normalizedContent, previousContent);
    if (!content) {
      return;
    }

    if (!this.isLoopContract(content) || !this.isMatchingPendingLoop(content)) {
      return;
    }

    const idx = pendingIndex;
    if (idx < 0) {
      return;
    }

    this.chatQuestions[idx] = {
      ...this.chatQuestions[idx],
      content
    };

    if (!this.chat?.id && content.audit?.chatId) {
      this.chat = {id: content.audit.chatId};
    }

    if (this.isTerminalLoopContent(content)) {
      this.isTyping = false;
      this.pendingQuestionIndex = -1;
      this.pendingClientRequestId = undefined;
    }

    setTimeout(() => {
      if (!this.isNearBottom()) {
        this.scrollToBottom();
      }
    });
  }

  aiExecute(content: AgentContent): void {
    if (!content.api) {
      return;
    }

    const placeholderIndex = this.chatQuestions.length;
    this.chatQuestions.push({
      question: this.translate.instant('ai.agent.actions.execute'),
      context: {}
    });
    this.isTyping = true;
    this.pendingQuestionIndex = placeholderIndex;
    this.pendingState = 'executing';

    const serviceUrl = `${content.api.url}?chatId=${this.chat?.id}`;
    switch (content.api.method?.toLowerCase()) {
      case 'get':
        this.get(serviceUrl).toPromise()
          .then((response: any) => this.handleAIAgentResponse(response, placeholderIndex))
          .catch(() => this.handleAIAgentResponse({
            question: this.translate.instant('ai.agent.actions.execute'),
            context: {},
            content: {
              mode: AgentLoopMode.ERROR,
              message: this.translate.instant('ai.agent.error.generic'),
              error: 'request_failed',
              operations: []
            }
          }, placeholderIndex));
        break;
      case 'post':
        this.post(serviceUrl, content.api.request).toPromise()
          .then((response: any) => this.handleAIAgentResponse(response, placeholderIndex))
          .catch(() => this.handleAIAgentResponse({
            question: this.translate.instant('ai.agent.actions.execute'),
            context: {},
            content: {
              mode: AgentLoopMode.ERROR,
              message: this.translate.instant('ai.agent.error.generic'),
              error: 'request_failed',
              operations: []
            }
          }, placeholderIndex));
        break;
      default:
        this.isTyping = false;
        this.pendingQuestionIndex = -1;
        console.warn('Method not supported');
    }
  }

  getPositionClass(): string {
    const classes: string[] = [];

    if (this.isDrawerOpened) {
      classes.push('ai-agent-drawer-open');
    }

    if (this.isDrawerOpened && this.isMenuExpanded) {
      classes.push('ai-agent-menu-expanded');
    }

    return classes.join(' ');
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.isMobileDrawerOpened = this.isDrawerOpened && this.isMobileViewport();
  }

  private isMobileViewport(): boolean {
    return window.matchMedia('(max-width: 767.98px)').matches;
  }

  getChangesSize(): number {
    return this.service.aiChanges.size;
  }

  startStep(content: AgentContent): void {
    if (!content?.actions?.length) {
      return;
    }

    this.agentContent = content;
    this.actualStep = this.resolveInitialStep(content.actions);
    this.view = ViewMode.CHAT;
    this.handleAction();
  }

  nextStep(): void {
    const indexes = this.getGuidedStepIndexes();
    if (!indexes.length) {
      this.moveToNextActionOrExit();
      return;
    }

    const currentListIndex = indexes.indexOf(this.activeGuidedFieldIndex);
    if (currentListIndex === -1) {
      this.setActiveGuidedField(indexes[0]);
      return;
    }

    if (currentListIndex >= indexes.length - 1) {
      this.moveToNextActionOrExit();
      return;
    }

    this.setActiveGuidedField(indexes[currentListIndex + 1]);
  }

  previousStep(): void {
    const indexes = this.getGuidedStepIndexes();
    const currentListIndex = indexes.indexOf(this.activeGuidedFieldIndex);

    if (currentListIndex > 0) {
      this.setActiveGuidedField(indexes[currentListIndex - 1]);
      return;
    }

    if (this.actualStep <= 1) {
      return;
    }

    this.actualStep -= 1;
    this.handleAction();
  }

  exitStep(): void {
    this.actualStep = 0;
    this.activeGuidedFieldIndex = -1;
    this.guidedNavigation = undefined;
    this.guidedFields = [];
    this.pendingGuidedAdvanceFieldId = undefined;
    this.clearGuidedHighlight();
    this.view = ViewMode.CHAT;
  }

  handleAction(): void {
    const action = this.getCurrentAction();
    if (!action) {
      this.service.dispachEvent({type: AgentEventType.ACTION, event: undefined});
      return;
    }

    this.service.dispachEvent({type: AgentEventType.ACTION, event: action});
    this.scheduleGuidedFieldRefresh();
  }

  onQuestionChange(event: any): void {
    this.question = event.target?.value;
  }

  undo(): void {
    this.service.dispachEvent({type: AgentEventType.UNDO});
    this.service.aiChanges = new Map<string, AgentChange>();
    this.guidedFields = this.guidedFields.map((guidedField) => ({
      ...guidedField,
      status: guidedField.status === 'filled' ? 'available' : guidedField.status,
      detail: guidedField.status === 'filled' ? this.getGuidedFieldDetail('available') : guidedField.detail
    }));

    if (this.activeGuidedFieldIndex < 0) {
      this.setActiveGuidedField(this.getNavigableFieldIndexes()[0] ?? -1);
    }
  }

  close(): void {
    this.cancelChatTitleEdit();
    this.exitStep();
    this.isTyping = false;
    this.pendingQuestionIndex = -1;
    this.view = ViewMode.FAB;
  }

  undoField(field: AgentField): void {
    this.service.undoField(field);
  }

  toggleChats(): void {
    this.view = this.view === ViewMode.HISTORY ? ViewMode.CHAT : ViewMode.HISTORY;
    if (this.view === ViewMode.HISTORY) {
      this.service.findChats().then(chats => {
        this.chats = chats;
      });
    }
  }

  trackByQuestion(index: number, question: AgentQuestion): string | number {
    return question.id || question.content?.audit?.correlationId || index;
  }

  trackByGuidedAction = (index: number, action: AgentAction): string | number => {
    return this.getGuidedActionKey(action) || index;
  };

  trackByAgentField = (index: number, field: AgentField): string | number => {
    return field.id || field.name || index;
  };

  trackByAttachment = (index: number, attachment: AgentAttachmentResult): string | number => {
    return attachment.localId || attachment.fileName || index;
  };

  onInputFocus(): void {
    if (this.view === ViewMode.FAB) {
      this.view = ViewMode.CHAT;
    }
  }

  openAttachmentPicker(): void {
    this.attachmentInput?.nativeElement.click();
  }

  onAttachmentInputChange(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const files = input?.files ? Array.from(input.files) : [];
    this.addFilesToComposer(files);
    if (input) {
      input.value = '';
    }
  }

  onComposerDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isAttachmentDragActive = true;
  }

  onComposerDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isAttachmentDragActive = false;
  }

  onComposerDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isAttachmentDragActive = false;
    const files = event.dataTransfer?.files ? Array.from(event.dataTransfer.files) : [];
    this.addFilesToComposer(files);
  }

  onComposerPaste(event: ClipboardEvent): void {
    const items = Array.from(event.clipboardData?.items || []);
    if (!items.length) {
      return;
    }

    const files: File[] = [];
    let hasUnsupportedFileItem = false;

    items.forEach((item, index) => {
      if (item.kind !== 'file') {
        return;
      }

      const file = item.getAsFile();
      if (!file) {
        hasUnsupportedFileItem = true;
        return;
      }

      const normalizedName = file.name || (file.type.startsWith('image/')
        ? `clipboard-image-${Date.now()}-${index + 1}.${this.getImageExtension(file.type)}`
        : `clipboard-file-${Date.now()}-${index + 1}`);
      files.push(new File([file], normalizedName, {type: file.type || 'application/octet-stream'}));
    });

    if (files.length > 0) {
      event.preventDefault();
      this.addFilesToComposer(files);
      return;
    }

    if (hasUnsupportedFileItem) {
      this.snackbar.info(this.translate.instant('ai.agent.attachments.clipboard_file_unsupported'));
    }
  }

  removeDraftAttachment(attachmentId: string): void {
    this.draftAttachments = this.draftAttachments.filter((attachment) => attachment.id !== attachmentId);
  }

  getQuestionAttachments(question: AgentQuestion): AgentAttachmentResult[] {
    return question.attachmentResults || [];
  }

  getAttachmentUiState(attachment: AgentAttachmentResult): AgentAttachmentUiState {
    if (attachment.uiState) {
      return attachment.uiState;
    }

    if (attachment.status === AgentAttachmentStatus.INCORPORATED) {
      return 'success';
    }

    if (attachment.status === AgentAttachmentStatus.FAILED) {
      return 'failed';
    }

    return 'pending';
  }

  getAttachmentStateLabel(attachment: AgentAttachmentResult): string {
    return this.translate.instant(`ai.agent.attachments.status.${this.getAttachmentUiState(attachment)}`);
  }

  getAttachmentStageLabel(stage?: AgentAttachmentProcessingStage): string {
    if (!stage) {
      return '';
    }

    return this.translate.instant(`ai.agent.attachments.stage.${stage.toLowerCase()}`);
  }

  getAttachmentKindLabel(kind?: AgentAttachmentKind): string {
    return this.translate.instant(`ai.agent.attachments.kind.${(kind || AgentAttachmentKind.DOCUMENT).toLowerCase()}`);
  }

  getAttachmentStateClass(attachment: AgentAttachmentResult): string {
    return `ai-agent-attachment-card--${this.getAttachmentUiState(attachment)}`;
  }

  getAttachmentIconClass(attachment: Pick<AgentAttachmentResult, 'kind' | 'contentType' | 'fileName'>): string {
    if (attachment.kind === AgentAttachmentKind.IMAGE || attachment.contentType?.startsWith('image/')) {
      return 'fa-solid fa-image';
    }

    const extension = (attachment.fileName || '').split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'fa-solid fa-file-pdf';
      case 'doc':
      case 'docx':
        return 'fa-solid fa-file-word';
      case 'xls':
      case 'xlsx':
      case 'csv':
        return 'fa-solid fa-file-excel';
      case 'ppt':
      case 'pptx':
        return 'fa-solid fa-file-powerpoint';
      case 'zip':
      case 'rar':
      case '7z':
        return 'fa-solid fa-file-zipper';
      default:
        return 'fa-solid fa-file-lines';
    }
  }

  formatAttachmentSize(size?: number): string {
    if (!size || size <= 0) {
      return '0 KB';
    }

    if (size >= 1024 * 1024) {
      return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    }

    return `${Math.max(1, Math.round(size / 1024))} KB`;
  }

  canEditSelectedChatTitle(): boolean {
    return !!this.chat?.id;
  }

  getHeaderTitle(): string {
    const firstQuestion = this.chatQuestions
      .find(chatQuestion => chatQuestion.question?.trim())
      ?.question
      ?.trim();

    return this.chat?.name || firstQuestion || this.question;
  }

  startChatTitleEdit(): void {
    if (!this.canEditSelectedChatTitle() || this.isSavingChatTitle) {
      return;
    }

    this.editingChatTitle = this.normalizeChatTitle(this.chat?.name || this.getHeaderTitle() || '');
    this.isEditingChatTitle = true;
    setTimeout(() => {
      this.chatTitleInput?.nativeElement.focus();
      this.chatTitleInput?.nativeElement.select();
    });
  }

  cancelChatTitleEdit(force = false): void {
    if (this.isSavingChatTitle && !force) {
      return;
    }

    this.isEditingChatTitle = false;
    this.editingChatTitle = this.chat?.name || this.getHeaderTitle() || '';
  }

  saveChatTitle(): void {
    if (!this.isEditingChatTitle || !this.chat?.id || this.isSavingChatTitle) {
      return;
    }

    const title = this.normalizeChatTitle(this.editingChatTitle);
    const currentTitle = this.normalizeChatTitle(this.chat?.name || this.getHeaderTitle() || '');

    if (!title || title === currentTitle) {
      this.cancelChatTitleEdit();
      return;
    }

    this.isSavingChatTitle = true;
    this.service.updateChatTitle(this.chat.id, title)
      .then((updatedChat: AgentChat) => {
        const nextChat: AgentChat = {
          ...this.chat,
          ...updatedChat,
          name: updatedChat?.name || title
        };
        this.chat = nextChat;
        this.chats = this.chats.map(chat => chat.id === nextChat.id ? {...chat, ...nextChat} : chat);
        this.isEditingChatTitle = false;
        this.editingChatTitle = nextChat.name || '';
      })
      .catch(() => {
        this.snackbar.error(this.translate.instant('ai.agent.error.generic'));
        this.cancelChatTitleEdit(true);
      })
      .finally(() => {
        this.isSavingChatTitle = false;
      });
  }

  changeChat(chat: AgentChat | undefined): void {
    this.cancelChatTitleEdit();
    this.isTyping = false;
    this.pendingQuestionIndex = -1;
    this.dismissedConfirmationKeys.clear();
    if (!chat?.id) {
      this.chat = undefined;
      this.chatQuestions = [];
    } else {
      this.service.findQuestionsByChat(chat.id).then((questions: AgentQuestion[]) => {
        this.chat = chat;
        questions.forEach(q => {
          q.content = this.parseAnswer(q.answer);
        });
        this.chatQuestions = questions;

        setTimeout(() => {
          if (!this.isNearBottom()) {
            this.scrollToBottom();
          }
        }, 1000);
      });
    }
    this.toggleChats();
  }

  fillField(guidedField: GuidedFieldView): void {
    if (!this.canFillGuidedField(guidedField)) {
      return;
    }

    try {
      this.service.dispachEvent({type: AgentEventType.FILL_FIELD, event: guidedField.field});
      this.guidedFields = this.guidedFields.map(item => item.field.id === guidedField.field.id ? {
        ...item,
        status: 'filled',
        detail: this.getGuidedFieldDetail('filled')
      } : item);
      this.pendingGuidedAdvanceFieldId = guidedField.field.id;
      this.scheduleGuidedFieldRefresh();
    } catch (error) {
      this.guidedFields = this.guidedFields.map(item => item.field.id === guidedField.field.id ? {
        ...item,
        status: 'error',
        detail: this.getGuidedFieldDetail('error')
      } : item);
      this.snackbar.error(this.translate.instant('ai.agent.error.generic'));
    }
  }

  isFieldActive(index: number): boolean {
    return this.activeGuidedFieldIndex === index;
  }

  getActiveGuidedField(): GuidedFieldView | undefined {
    return this.activeGuidedFieldIndex >= 0 ? this.guidedFields[this.activeGuidedFieldIndex] : undefined;
  }

  hasNavigableFields(): boolean {
    return this.getGuidedStepIndexes().length > 0;
  }

  canFillGuidedField(guidedField: GuidedFieldView): boolean {
    return guidedField.status === 'available' && this.hasGuidedFieldValue(guidedField);
  }

  getActiveGuidedFieldOrder(): number {
    const indexes = this.getGuidedStepIndexes();
    const currentListIndex = indexes.indexOf(this.activeGuidedFieldIndex);
    return currentListIndex >= 0 ? currentListIndex + 1 : 0;
  }

  getGuidedFieldCount(): number {
    return this.getGuidedStepIndexes().length;
  }

  hasStepContent(): boolean {
    return !!this.guidedNavigation || this.guidedFields.length > 0 || this.isCurrentActionWithoutFields();
  }

  shouldShowExecute(content: AgentContent | undefined): boolean {
    return !this.isLoopContract(content) && !!content?.api;
  }

  shouldShowGuidedStart(content: AgentContent | undefined): boolean {
    return (content?.actions?.length || 0) > 0;
  }

  canGoToPreviousStep(): boolean {
    const indexes = this.getGuidedStepIndexes();
    const currentListIndex = indexes.indexOf(this.activeGuidedFieldIndex);

    if (currentListIndex > 0) {
      return true;
    }

    return this.actualStep > 1;
  }

  shouldShowNextStep(): boolean {
    return this.hasNavigableFields() || this.hasNextAction() || this.isCurrentActionWithoutFields() || !!this.guidedNavigation;
  }

  isLoopContract(content: AgentContent | undefined): boolean {
    return !!content && (
      !!this.normalizeLoopMode(content.mode)
      || !!content.loop
      || !!content.operations?.length
      || !!content.planSummary
      || !!content.executionSummary
      || !!content.audit
      || !!content.trace?.length
    );
  }

  getLoopSummary(content: AgentContent | undefined): string | undefined {
    if (!content) {
      return undefined;
    }

    return content.planSummary || content.executionSummary || undefined;
  }

  getLoopSummaryLabel(content: AgentContent | undefined): string {
    if (this.resolveLoopMode(content) === AgentLoopMode.AWAITING_CONFIRMATION) {
      return this.translate.instant('ai.agent.loop.plan_summary');
    }

    return this.translate.instant('ai.agent.loop.execution_summary');
  }

  getLoopOperations(content: AgentContent | undefined): AgentOperation[] {
    if (!content) {
      return [];
    }

    const operationsByKey = new Map<string, AgentOperation>();
    (content.trace || []).forEach((traceStep) => {
      (traceStep.operations || []).forEach((operation, index) => {
        operationsByKey.set(this.getOperationKey(operation, traceStep.step, index), operation);
      });
    });

    (content.operations || []).forEach((operation, index) => {
      operationsByKey.set(this.getOperationKey(operation, content.loop?.step, index), operation);
    });

    return Array.from(operationsByKey.values());
  }

  getLoopResults(content: AgentContent | undefined): AgentOperationResult[] {
    const results: AgentOperationResult[] = [];
    (content?.trace || []).forEach((traceStep: AgentTraceStep) => {
      results.push(...(traceStep.results || []));
    });
    return results;
  }

  getLoopPlanItems(content: AgentContent | undefined): AgentPlanItem[] {
    if (!content) {
      return [];
    }

    const cachedItems = this.loopPlanItemsCache.get(content);
    if (cachedItems) {
      return cachedItems;
    }

    const operations = this.getLoopOperations(content);
    const results = this.getLoopResults(content);

    const items = operations.map((operation) => {
      const result = this.findOperationResult(operation, results);
      return {
        operation,
        result,
        status: this.resolvePlanItemStatus(content, operation, result)
      };
    });

    this.loopPlanItemsCache.set(content, items);
    return items;
  }

  hasLoopPlanItems(content: AgentContent | undefined): boolean {
    return this.getLoopPlanItems(content).length > 0;
  }

  getGuidedActions(content: AgentContent | undefined): AgentAction[] {
    return content?.actions || [];
  }

  hasGuidedActions(content: AgentContent | undefined): boolean {
    return this.getGuidedActions(content).length > 0;
  }

  getGuidedActionTitle(action: AgentAction, index: number): string {
    return action.message?.trim()
      || action.url?.trim()
      || `${this.translate.instant('ai.agent.actions.guided_list.item')} ${index + 1}`;
  }

  getPlanItemIconClass(item: AgentPlanItem): string {
    switch (item.status) {
      case 'success':
        return 'fas fa-check-circle text-success check-done fa-lg';
      case 'error':
        return 'fas fa-times-circle text-danger check-error fa-lg';
      case 'running':
        return 'fas fa-circle-notch fa-spin text-primary check-running fa-lg';
      default:
        return 'far fa-check-circle check-not-done fa-lg';
    }
  }

  getPlanItemTitle(item: AgentPlanItem): string {
    return this.getOperationTitle(item.operation);
  }

  canConfirmPlan(content: AgentContent | undefined, questionIndex: number): boolean {
    if (!content || this.resolveLoopMode(content) !== AgentLoopMode.AWAITING_CONFIRMATION) {
      return false;
    }

    if ((content.operations?.length || 0) === 0) {
      return false;
    }

    return !this.dismissedConfirmationKeys.has(this.getLoopQuestionKey(questionIndex, content));
  }

  confirmPlan(content: AgentContent | undefined, questionIndex: number): void {
    if (!content || !this.canConfirmPlan(content, questionIndex)) {
      return;
    }

    this.dismissedConfirmationKeys.delete(this.getLoopQuestionKey(questionIndex, content));
    const clientRequestId = this.createClientRequestId();
    this.isTyping = true;
    this.pendingQuestionIndex = questionIndex;
    this.pendingState = 'executing';
    this.pendingClientRequestId = clientRequestId;
    this.chatQuestions[questionIndex] = {
      ...this.chatQuestions[questionIndex],
      content: this.buildRunningPlanContent(content, clientRequestId)
    };

    this.service.confirmPlan(AiAgentComponent.EXPLICIT_CONFIRMATION_PROMPT, this.chat?.id, clientRequestId)
      .then((response: AgentQuestion) => this.handleAIAgentResponse(response, questionIndex))
      .catch(() => this.handleAIAgentResponse({
        question: this.chatQuestions[questionIndex]?.question || AiAgentComponent.EXPLICIT_CONFIRMATION_PROMPT,
        context: {},
        content: {
          mode: AgentLoopMode.ERROR,
          message: this.translate.instant('ai.agent.error.generic'),
          error: 'request_failed',
          operations: []
        }
      }, questionIndex));
  }

  cancelPlan(content: AgentContent | undefined, questionIndex: number): void {
    if (!content) {
      return;
    }

    this.dismissedConfirmationKeys.add(this.getLoopQuestionKey(questionIndex, content));
    this.focusInput();
  }

  getOperationTitle(operation: AgentOperation): string {
    if (operation.reason?.trim()) {
      return operation.reason.trim();
    }

    switch (operation.handler) {
      case 'tasks.list_upcoming':
        return this.translate.instant('ai.agent.loop.handlers.tasks_list_upcoming');
      case 'appointments.list_upcoming':
        return this.translate.instant('ai.agent.loop.handlers.appointments_list_upcoming');
      case 'tasks.reschedule_batch':
        return this.translate.instant('ai.agent.loop.handlers.tasks_reschedule_batch');
      case 'appointments.reschedule_batch':
        return this.translate.instant('ai.agent.loop.handlers.appointments_reschedule_batch');
      default:
        return this.translate.instant('ai.agent.loop.handlers.generic');
    }
  }

  isPendingQuestion(index: number): boolean {
    return this.isTyping && this.pendingQuestionIndex === index;
  }

  getPendingMessage(index: number): string {
    if (!this.isPendingQuestion(index)) {
      return '';
    }

    return this.translate.instant(`ai.agent.loop.pending.${this.pendingState === 'executing' ? 'executing' : 'thinking'}`);
  }

  getPendingAvatarState(): AiAgentAvatarState {
    return this.pendingState === 'executing' ? 'executing' : 'thinking';
  }

  getQuestionAvatarState(question: AgentQuestion, questionIndex: number): AiAgentAvatarState {
    if (this.isPendingQuestion(questionIndex)) {
      return this.getPendingAvatarState();
    }

    if (!question?.content) {
      return 'idle';
    }

    return this.getContentAvatarState(question.content);
  }

  private handleAIAgentResponse(response: AgentQuestion, idx: number): void {
    const previousContent = idx >= 0 ? this.chatQuestions[idx]?.content : undefined;
    const previousAttachments = idx >= 0 ? this.chatQuestions[idx]?.attachmentResults : [];
    const normalizedContent = response.content ? this.normalizeAgentContent(response.content) : this.parseAnswer(response.answer);
    response.content = this.mergeGuidedActions(normalizedContent, previousContent);
    response.attachmentResults = this.normalizeAttachmentResults(response.attachmentResults, previousAttachments);

    if (idx >= 0) {
      this.chatQuestions[idx] = {...response};
    } else {
      this.chatQuestions.push(response);
    }
    this.isTyping = false;
    this.pendingQuestionIndex = -1;
    this.pendingClientRequestId = undefined;

    setTimeout(() => {
      if (!this.isNearBottom()) {
        this.scrollToBottom();
      }
    }, 400);
  }

  private createClientRequestId(): string {
    const cryptoRef = window.crypto as Crypto | undefined;
    if (cryptoRef?.randomUUID) {
      return cryptoRef.randomUUID();
    }

    return `ai-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  private isMatchingPendingLoop(content: AgentContent): boolean {
    const audit = content.audit;

    if (this.pendingClientRequestId && audit?.clientRequestId) {
      return audit.clientRequestId === this.pendingClientRequestId;
    }

    if (this.chat?.id && audit?.chatId) {
      return audit.chatId === this.chat.id;
    }

    return this.isTyping && this.pendingQuestionIndex >= 0;
  }

  private resolvePendingLoopIndex(content: AgentContent): number {
    if (this.pendingClientRequestId && content.audit?.clientRequestId === this.pendingClientRequestId) {
      return this.pendingQuestionIndex;
    }

    const index = this.chatQuestions.findIndex(question => question.content?.audit?.correlationId
      && question.content.audit.correlationId === content.audit?.correlationId);
    if (index >= 0) {
      return index;
    }

    return this.pendingQuestionIndex;
  }

  private isTerminalLoopContent(content: AgentContent): boolean {
    const mode = this.resolveLoopMode(content);
    return mode === AgentLoopMode.FINAL_ANSWER
      || mode === AgentLoopMode.EXECUTION_RESULT
      || mode === AgentLoopMode.AWAITING_CONFIRMATION
      || mode === AgentLoopMode.ERROR;
  }

  private parseAnswer(answer: string | undefined): AgentContent | undefined {
    if (!answer) {
      return;
    }

    try {
      return this.normalizeAgentContent(JSON.parse(answer));
    } catch (e) {
      console.warn(e);
      return {error: this.translate.instant('ai.agent.error.generic')};
    }
  }

  private normalizeAttachmentResults(results: AgentAttachmentResult[] | undefined, fallback: AgentAttachmentResult[] = []): AgentAttachmentResult[] {
    const source = results?.length ? results : fallback;
    return (source || []).map((attachment) => ({
      ...attachment,
      kind: this.normalizeAttachmentKind(attachment.kind),
      status: this.normalizeAttachmentStatus(attachment.status),
      stage: this.normalizeAttachmentStage(attachment.stage),
      limitations: attachment.limitations || [],
      imageAnalysis: attachment.imageAnalysis ? {
        ...attachment.imageAnalysis,
        limitations: attachment.imageAnalysis.limitations || []
      } : undefined,
      uiState: attachment.uiState || this.resolveAttachmentUiState(attachment)
    }));
  }

  private normalizeAgentContent(content: AgentContent): AgentContent {
    const request = content?.api?.request ?? {};

    return {
      ...content,
      error: content?.error !== undefined ? this.translateAgentError(content.error) : undefined,
      mode: this.normalizeLoopMode(content?.mode),
      loop: content?.loop ? {
        ...content.loop,
        continueLoop: this.resolveLoopContinueFlag(content.loop)
      } : undefined,
      operations: content?.operations?.map(operation => this.normalizeOperation(operation)) || [],
      trace: content?.trace?.map(traceStep => this.normalizeTraceStep(traceStep)) || [],
      actions: (content.actions || []).map((action) => ({
        ...action,
        fields: action.fields?.map((field) => {
          if (!field?.id) {
            return field;
          }

          const fallbackValue = request[field.id];
          const normalizedValue = field.value !== undefined && field.value !== null && field.value !== ''
            ? field.value
            : fallbackValue;

          return normalizedValue === undefined ? field : {
            ...field,
            value: normalizedValue
          };
        }) || []
      }))
    };
  }

  private translateAgentError(error: string | undefined): string {
    const genericMessage = this.translate.instant('ai.agent.error.generic');
    const normalizedError = error?.toString().trim().toLowerCase();

    if (!normalizedError) {
      return genericMessage;
    }

    const translationKey = `ai.agent.error.messages.${normalizedError}`;
    const translatedMessage = this.translate.instant(translationKey);

    return translatedMessage !== translationKey ? translatedMessage : genericMessage;
  }

  private normalizeTraceStep(traceStep: AgentTraceStep): AgentTraceStep {
    return {
      ...traceStep,
      mode: this.normalizeLoopMode(traceStep?.mode),
      operations: traceStep?.operations?.map(operation => this.normalizeOperation(operation)) || [],
      results: traceStep?.results?.map(result => ({
        ...result,
        kind: this.normalizeOperationKind(result?.kind)
      })) || []
    };
  }

  private normalizeOperation(operation: AgentOperation): AgentOperation {
    return {
      ...operation,
      kind: this.normalizeOperationKind(operation?.kind),
      status: this.normalizeOperationStatus(operation?.status)
    };
  }

  private normalizeLoopMode(mode: AgentLoopMode | string | undefined): AgentLoopMode | undefined {
    switch ((mode || '').toString().toUpperCase()) {
      case AgentLoopMode.FINAL_ANSWER:
        return AgentLoopMode.FINAL_ANSWER;
      case AgentLoopMode.COLLECT_DATA:
        return AgentLoopMode.COLLECT_DATA;
      case AgentLoopMode.AWAITING_CONFIRMATION:
        return AgentLoopMode.AWAITING_CONFIRMATION;
      case AgentLoopMode.EXECUTION_RESULT:
        return AgentLoopMode.EXECUTION_RESULT;
      case AgentLoopMode.ERROR:
        return AgentLoopMode.ERROR;
      default:
        return undefined;
    }
  }

  private normalizeOperationKind(kind: AgentOperationKind | string | undefined): AgentOperationKind | undefined {
    switch ((kind || '').toString().toUpperCase()) {
      case AgentOperationKind.READ:
        return AgentOperationKind.READ;
      case AgentOperationKind.WRITE:
        return AgentOperationKind.WRITE;
      default:
        return undefined;
    }
  }

  private normalizeOperationStatus(status: AgentOperationStatus | string | undefined): AgentOperationStatus | undefined {
    switch ((status || '').toString().toUpperCase()) {
      case AgentOperationStatus.PENDING:
        return AgentOperationStatus.PENDING;
      case AgentOperationStatus.RUNNING:
        return AgentOperationStatus.RUNNING;
      case AgentOperationStatus.SUCCESS:
        return AgentOperationStatus.SUCCESS;
      case AgentOperationStatus.ERROR:
        return AgentOperationStatus.ERROR;
      default:
        return undefined;
    }
  }

  private normalizeAttachmentKind(kind: AgentAttachmentKind | string | undefined): AgentAttachmentKind | undefined {
    switch ((kind || '').toString().toUpperCase()) {
      case AgentAttachmentKind.IMAGE:
        return AgentAttachmentKind.IMAGE;
      case AgentAttachmentKind.DOCUMENT:
        return AgentAttachmentKind.DOCUMENT;
      default:
        return undefined;
    }
  }

  private normalizeAttachmentStatus(status: AgentAttachmentStatus | string | undefined): AgentAttachmentStatus | undefined {
    switch ((status || '').toString().toUpperCase()) {
      case AgentAttachmentStatus.INCORPORATED:
        return AgentAttachmentStatus.INCORPORATED;
      case AgentAttachmentStatus.FAILED:
        return AgentAttachmentStatus.FAILED;
      default:
        return undefined;
    }
  }

  private normalizeAttachmentStage(stage: AgentAttachmentProcessingStage | string | undefined): AgentAttachmentProcessingStage | undefined {
    switch ((stage || '').toString().toUpperCase()) {
      case AgentAttachmentProcessingStage.VALIDATION:
        return AgentAttachmentProcessingStage.VALIDATION;
      case AgentAttachmentProcessingStage.TEXT_EXTRACTION:
        return AgentAttachmentProcessingStage.TEXT_EXTRACTION;
      case AgentAttachmentProcessingStage.IMAGE_ANALYSIS:
        return AgentAttachmentProcessingStage.IMAGE_ANALYSIS;
      default:
        return undefined;
    }
  }

  private resolveAttachmentUiState(attachment: AgentAttachmentResult): AgentAttachmentUiState {
    if (attachment.status === AgentAttachmentStatus.INCORPORATED) {
      return 'success';
    }

    if (attachment.status === AgentAttachmentStatus.FAILED) {
      return 'failed';
    }

    return 'pending';
  }

  private resolveLoopMode(content: AgentContent | undefined): AgentLoopMode | undefined {
    return this.normalizeLoopMode(content?.mode);
  }

  private getContentAvatarState(content: AgentContent | undefined): AiAgentAvatarState {
    const mode = this.resolveLoopMode(content);

    if (mode === AgentLoopMode.COLLECT_DATA) {
      return 'processing';
    }

    if (this.getLoopPlanItems(content).some(item => item.status === 'running')) {
      return 'executing';
    }

    return 'idle';
  }

  private resolveLoopContinueFlag(loop: any): boolean | undefined {
    if (!loop) {
      return undefined;
    }

    if (loop.continueLoop !== undefined) {
      return !!loop.continueLoop;
    }

    if (loop.continue !== undefined) {
      return !!loop.continue;
    }

    return undefined;
  }

  getLoopVisualState(content: AgentContent | undefined): LoopVisualState {
    const mode = this.resolveLoopMode(content);
    if (!mode) {
      return 'final_answer';
    }

    if (this.getLoopPlanItems(content).some(item => item.status === 'running')) {
      return 'executing';
    }

    if (mode === AgentLoopMode.ERROR) {
      return 'error';
    }

    if (mode === AgentLoopMode.COLLECT_DATA) {
      return 'collect_data';
    }

    if (mode === AgentLoopMode.AWAITING_CONFIRMATION) {
      return 'awaiting_confirmation';
    }

    if (mode === AgentLoopMode.EXECUTION_RESULT) {
      const results = this.getLoopResults(content);
      const successCount = results.filter(result => result.success === true).length;
      const errorCount = results.filter(result => result.success === false).length;

      if (errorCount === 0) {
        return 'success';
      }

      if (successCount > 0) {
        return 'partial_success';
      }

      return 'error';
    }

    return 'final_answer';
  }

  private findOperationResult(operation: AgentOperation, results: AgentOperationResult[]): AgentOperationResult | undefined {
    for (let index = results.length - 1; index >= 0; index -= 1) {
      const result = results[index];
      if ((!!operation.id && result.id === operation.id)
        || (!!operation.handler && result.handler === operation.handler)) {
        return result;
      }
    }

    return undefined;
  }

  private resolvePlanItemStatus(content: AgentContent | undefined,
                                operation: AgentOperation,
                                result?: AgentOperationResult): AgentPlanItemStatus {
    if (result?.success === true) {
      return 'success';
    }

    if (result?.success === false) {
      return 'error';
    }

    switch (operation.status) {
      case AgentOperationStatus.SUCCESS:
        return 'success';
      case AgentOperationStatus.ERROR:
        return 'error';
      case AgentOperationStatus.RUNNING:
        return 'running';
      case AgentOperationStatus.PENDING:
        return 'pending';
      default:
        break;
    }

    const mode = this.resolveLoopMode(content);
    if (mode === AgentLoopMode.COLLECT_DATA) {
      return 'running';
    }

    if (mode === AgentLoopMode.ERROR) {
      return 'error';
    }

    return 'pending';
  }

  private buildRunningPlanContent(content: AgentContent, clientRequestId: string): AgentContent {
    let hasRunningOperation = false;

    return this.normalizeAgentContent({
      ...content,
      mode: AgentLoopMode.EXECUTION_RESULT,
      executionSummary: content.executionSummary || content.planSummary,
      audit: {
        ...(content.audit || {}),
        clientRequestId
      },
      operations: (content.operations || []).map(operation => {
        if (!hasRunningOperation) {
          hasRunningOperation = true;
          return {
            ...operation,
            status: AgentOperationStatus.RUNNING
          };
        }

        return {
          ...operation,
          status: AgentOperationStatus.PENDING
        };
      }),
      trace: content.trace || []
    });
  }

  private mergeGuidedActions(content: AgentContent | undefined,
                             previousContent: AgentContent | undefined): AgentContent | undefined {
    if (!content) {
      return content;
    }

    if ((content.actions?.length || 0) === 0 && (previousContent?.actions?.length || 0) === 0) {
      return content;
    }

    const actionsByKey = new Map<string, AgentAction>();
    (previousContent?.actions || []).forEach((action, index) => {
      actionsByKey.set(this.getGuidedActionKey(action, index), action);
    });
    (content.actions || []).forEach((action, index) => {
      actionsByKey.set(this.getGuidedActionKey(action, index), action);
    });

    return {
      ...content,
      actions: Array.from(actionsByKey.values())
    };
  }

  private getOperationKey(operation: AgentOperation, step: number | undefined, index: number): string {
    const params = operation?.params ? JSON.stringify(operation.params) : '';
    return [
      operation?.id || '',
      operation?.handler || '',
      operation?.reason || '',
      params,
      step ?? '',
      index
    ].join('|');
  }

  private getGuidedActionKey(action: AgentAction, index?: number): string {
    const fieldsKey = (action.fields || [])
      .map(field => `${field.id || ''}:${field.name || ''}:${field.value ?? ''}:${field.type || ''}`)
      .join(',');

    return [
      action.url || '',
      action.htmlFile || '',
      action.message || '',
      fieldsKey,
      index ?? ''
    ].join('|');
  }

  private getLoopQuestionKey(index: number, content: AgentContent): string {
    return content.audit?.correlationId || content.id || `loop-question-${index}`;
  }

  private focusInput(): void {
    setTimeout(() => {
      this.view = ViewMode.CHAT;
      if (this.aiInput?.nativeElement) {
        this.aiInput.nativeElement.focus();
      }
    });
  }

  private scheduleGuidedFieldRefresh(retry = 0): void {
    if (this.guidedRefreshTimeout) {
      clearTimeout(this.guidedRefreshTimeout);
    }

    this.guidedRefreshTimeout = setTimeout(() => this.refreshGuidedFields(retry), 200);
  }

  private refreshGuidedFields(retry = 0): void {
    let action = this.getCurrentAction();
    if (!action) {
      return;
    }

    const advancedAction = this.advanceNavigationStepIfNeeded(action);
    if (advancedAction !== action) {
      action = advancedAction;
      if (!action) {
        this.guidedNavigation = undefined;
        this.guidedFields = [];
        this.activeGuidedFieldIndex = -1;
        this.clearGuidedHighlight();
        return;
      }
    }

    const guidedNavigation = this.resolveGuidedNavigation(action);
    if (guidedNavigation) {
      this.guidedNavigation = guidedNavigation;
      this.guidedFields = [];
      this.activeGuidedFieldIndex = -1;

      const highlighted = this.highlightGuidedTarget(guidedNavigation.targetId);
      if (!highlighted && retry < AiAgentComponent.GUIDED_FIELD_REFRESH_MAX_RETRIES) {
        this.scheduleGuidedFieldRefresh(retry + 1);
      }
      return;
    }

    this.guidedNavigation = undefined;

    const previousStatuses = new Map(this.guidedFields.map(field => [field.field.id, field.status]));
    const builtGuidedFields = action.fields?.map(field => this.buildGuidedField(field, action, previousStatuses.get(field.id))) || [];
    this.guidedFields = builtGuidedFields.filter(field => this.shouldDisplayGuidedField(field));

    const stepIndexes = this.getGuidedStepIndexes();
    const hasPotentialTargets = builtGuidedFields.length > 0;

    if (this.pendingGuidedAdvanceFieldId) {
      const currentFieldIndex = this.guidedFields.findIndex(field => field.field.id === this.pendingGuidedAdvanceFieldId);
      const nextStepIndex = stepIndexes.find(index => index > currentFieldIndex);
      this.pendingGuidedAdvanceFieldId = undefined;

      if (nextStepIndex !== undefined) {
        this.setActiveGuidedField(nextStepIndex);
        return;
      }

      if (stepIndexes.length > 0) {
        this.setActiveGuidedField(stepIndexes[0]);
        return;
      }
    }

    if (stepIndexes.length > 0) {
      const preferredIndex = stepIndexes.includes(this.activeGuidedFieldIndex)
        ? this.activeGuidedFieldIndex
        : stepIndexes[0];
      this.setActiveGuidedField(preferredIndex);
      return;
    }

    if (hasPotentialTargets && retry < AiAgentComponent.GUIDED_FIELD_REFRESH_MAX_RETRIES) {
      this.scheduleGuidedFieldRefresh(retry + 1);
      return;
    }

    this.activeGuidedFieldIndex = -1;
    this.clearGuidedHighlight();
  }

  private buildGuidedField(field: AgentField, action: AgentAction, previousStatus?: GuidedFieldStatus): GuidedFieldView {
    const targetId = this.resolveTargetId(action, field);
    const target = targetId ? document.querySelector(`[data-tour-id="${targetId}"]`) as HTMLElement | null : null;
    const interactiveTarget = this.resolveInteractiveTarget(target);
    const supportedContainer = this.isSupportedContainerTarget(target);

    let status: GuidedFieldStatus = 'available';
    if (previousStatus === 'filled') {
      status = 'filled';
    } else if (!targetId || !target) {
      status = 'target_not_found';
    } else if (!this.isGuidedFieldAvailable(target, interactiveTarget, supportedContainer)) {
      status = 'unavailable';
    }

    return {
      field,
      targetId,
      status,
      detail: this.getGuidedFieldDetail(status)
    };
  }

  private resolveTargetId(action: AgentAction, field: AgentField): string | undefined {
    if (field.targetId?.trim()) {
      return field.targetId.trim();
    }

    return AiAgentComponent.GUIDED_TARGETS[this.normalizeHtmlFilePath(action.htmlFile)]?.[field.id];
  }

  private normalizeChatTitle(title: string): string {
    return (title || '').trim().slice(0, AiAgentComponent.CHAT_TITLE_MAX_LENGTH);
  }

  private resolveInteractiveTarget(target: HTMLElement | null): HTMLElement | null {
    if (!target) {
      return null;
    }

    const selectors = [
      'input',
      'textarea',
      'select',
      '.ql-editor',
      '[contenteditable="true"]',
      '[role="textbox"]'
    ];

    for (const selector of selectors) {
      if (target.matches(selector) && this.isVisibleElement(target)) {
        return target;
      }

      const candidate = Array.from(target.querySelectorAll<HTMLElement>(selector))
        .find(element => this.isVisibleElement(element));
      if (candidate) {
        return candidate;
      }
    }

    return null;
  }

  private isSupportedContainerTarget(target: HTMLElement | null): boolean {
    if (!target) {
      return false;
    }

    return !!target.querySelector('app-rich-text, .rich-text, .ql-container, .ql-editor');
  }

  private isGuidedFieldAvailable(target: HTMLElement, interactiveTarget: HTMLElement | null, supportedContainer: boolean): boolean {
    if (!this.isVisibleElement(target) || this.isFieldUnavailable(target)) {
      return false;
    }

    if (interactiveTarget) {
      return !this.isFieldUnavailable(interactiveTarget);
    }

    return supportedContainer;
  }

  private isVisibleElement(target: HTMLElement): boolean {
    const style = window.getComputedStyle(target);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  private isFieldUnavailable(target: HTMLElement): boolean {
    if (target.hasAttribute('readonly') || target.getAttribute('aria-readonly') === 'true') {
      return true;
    }

    if (target.hasAttribute('disabled') || target.getAttribute('aria-disabled') === 'true') {
      return true;
    }

    const style = window.getComputedStyle(target);
    return style.display === 'none' || style.visibility === 'hidden';
  }

  private getGuidedFieldDetail(status: GuidedFieldStatus): string {
    return this.translate.instant(`ai.agent.actions.fields_list.status.${status}`);
  }

  private getCurrentAction(): AgentAction | undefined {
    return this.agentContent?.actions?.[this.actualStep - 1];
  }

  private resolveInitialStep(actions: AgentAction[]): number {
    const currentUrl = this.normalizeUrl(this.router.url);
    let matchingStep = 1;

    actions.forEach((action, index) => {
      const targetUrl = this.normalizeUrl(action.url);
      if (this.isCurrentRouteCompatible(currentUrl, targetUrl)) {
        matchingStep = index + 1;
      }
    });

    return matchingStep;
  }

  private hasNextAction(): boolean {
    return this.actualStep < (this.agentContent?.actions?.length || 0);
  }

  private isCurrentActionWithoutFields(): boolean {
    return (this.getCurrentAction()?.fields?.length || 0) === 0;
  }

  private moveToNextActionOrExit(): void {
    if (!this.hasNextAction()) {
      this.exitStep();
      return;
    }

    this.actualStep += 1;
    this.handleAction();
  }

  private isCurrentRouteCompatible(currentUrl: string, targetUrl: string): boolean {
    if (currentUrl === targetUrl) {
      return true;
    }

    if (targetUrl === '/' || !targetUrl) {
      return false;
    }

    return currentUrl.startsWith(`${targetUrl}/`);
  }

  private getNavigableFieldIndexes(): number[] {
    return this.guidedFields
      .map((field, index) => ({field, index}))
      .filter(item => item.field.status === 'available' && this.hasGuidedFieldValue(item.field))
      .map(item => item.index);
  }

  private getGuidedStepIndexes(): number[] {
    return this.guidedFields
      .map((field, index) => ({field, index}))
      .filter(item => item.field.status === 'available' || item.field.status === 'filled')
      .map(item => item.index);
  }

  private shouldDisplayGuidedField(guidedField: GuidedFieldView): boolean {
    return guidedField.status === 'available' || guidedField.status === 'filled';
  }

  private hasGuidedFieldValue(guidedField: GuidedFieldView): boolean {
    return guidedField.field.value !== undefined
      && guidedField.field.value !== null
      && `${guidedField.field.value}`.trim() !== '';
  }

  private setActiveGuidedField(index: number): void {
    if (index < 0 || !this.guidedFields[index]) {
      this.activeGuidedFieldIndex = -1;
      this.clearGuidedHighlight();
      return;
    }

    this.activeGuidedFieldIndex = index;
    this.highlightGuidedField(this.guidedFields[index]);
  }

  private highlightGuidedField(guidedField: GuidedFieldView): void {
    this.clearGuidedHighlight();

    if (!guidedField.targetId) {
      return;
    }

    this.highlightGuidedTarget(guidedField.targetId);
  }

  private clearGuidedHighlight(): void {
    document.querySelectorAll(`.${AiAgentComponent.GUIDED_HIGHLIGHT_CLASS}`)
      .forEach(el => el.classList.remove(AiAgentComponent.GUIDED_HIGHLIGHT_CLASS));
    if (this.guidedOverlaySyncTimeout) {
      clearTimeout(this.guidedOverlaySyncTimeout);
      this.guidedOverlaySyncTimeout = undefined;
    }
    document.querySelector(`.${AiAgentComponent.GUIDED_OVERLAY_CLASS}`)?.remove();
  }

  private highlightGuidedTarget(targetId: string): boolean {
    this.clearGuidedHighlight();

    const element = document.querySelector(`[data-tour-id="${targetId}"]`) as HTMLElement | null;
    if (!element) {
      return false;
    }

    element.classList.add(AiAgentComponent.GUIDED_HIGHLIGHT_CLASS);
    element.scrollIntoView({behavior: 'smooth', block: 'center', inline: 'nearest'});
    this.ensureGuidedOverlay(element);
    this.guidedOverlaySyncTimeout = setTimeout(() => this.syncGuidedOverlay(element), 250);
    return true;
  }

  private ensureGuidedOverlay(target: HTMLElement): void {
    let overlay = document.querySelector(`.${AiAgentComponent.GUIDED_OVERLAY_CLASS}`) as HTMLDivElement | null;
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = AiAgentComponent.GUIDED_OVERLAY_CLASS;
      document.body.appendChild(overlay);
    }

    this.syncGuidedOverlay(target, overlay);
  }

  private syncGuidedOverlay(target: HTMLElement, overlay?: HTMLDivElement): void {
    const targetOverlay = overlay || document.querySelector(`.${AiAgentComponent.GUIDED_OVERLAY_CLASS}`) as HTMLDivElement | null;
    if (!targetOverlay || !document.body.contains(target)) {
      return;
    }

    const rect = target.getBoundingClientRect();
    const padding = 6;
    targetOverlay.style.top = `${Math.max(rect.top - padding, 0)}px`;
    targetOverlay.style.left = `${Math.max(rect.left - padding, 0)}px`;
    targetOverlay.style.width = `${Math.max(rect.width + (padding * 2), 0)}px`;
    targetOverlay.style.height = `${Math.max(rect.height + (padding * 2), 0)}px`;
  }

  private resolveGuidedNavigation(action: AgentAction): GuidedNavigationView | undefined {
    const currentUrl = this.normalizeUrl(this.router.url);
    const targetUrl = this.normalizeUrl(action.url);

    if (currentUrl === targetUrl) {
      return undefined;
    }

    if (targetUrl === '/tasks') {
      return {
        targetId: 'menu-tasks',
        title: 'Menu',
        value: 'Tarefas',
        description: 'Acesse o módulo de tarefas para continuar.'
      };
    }

    if (targetUrl === '/tasks/add') {
      if (!currentUrl.startsWith('/tasks')) {
        return {
          targetId: 'menu-tasks',
          title: 'Menu',
          value: 'Tarefas',
          description: 'Abra a tela de tarefas para continuar.'
        };
      }

      return {
        targetId: 'task-create-button',
        title: 'Ação',
        value: 'Nova tarefa',
        description: 'Use este botão para abrir o cadastro da tarefa.'
      };
    }

    if (targetUrl === '/customers') {
      return {
        targetId: 'menu-customers',
        title: 'Menu',
        value: 'Clientes',
        description: 'Acesse o módulo de clientes para continuar.'
      };
    }

    if (targetUrl === '/customers/add') {
      if (!currentUrl.startsWith('/customers')) {
        return {
          targetId: 'menu-customers',
          title: 'Menu',
          value: 'Clientes',
          description: 'Abra a tela de clientes para continuar.'
        };
      }

      return {
        targetId: 'customer-create-button',
        title: 'Ação',
        value: 'Novo cliente',
        description: 'Use este botão para abrir o cadastro do cliente.'
      };
    }

    return undefined;
  }

  private advanceNavigationStepIfNeeded(action: AgentAction): AgentAction | undefined {
    const currentUrl = this.normalizeUrl(this.router.url);
    const targetUrl = this.normalizeUrl(action.url);
    const isNavigationOnlyAction = (action.fields?.length || 0) === 0;

    if (!isNavigationOnlyAction || currentUrl !== targetUrl) {
      return action;
    }

    const nextStep = this.actualStep + 1;
    const totalSteps = this.agentContent?.actions?.length || 0;

    if (nextStep > totalSteps) {
      return undefined;
    }

    this.actualStep = nextStep;
    return this.getCurrentAction();
  }

  private normalizeUrl(url: string): string {
    const sanitizedUrl = (url || '').trim().split('?')[0];
    const hashRouteMatch = sanitizedUrl.match(/#(\/.*)$/);
    const normalizedUrl = hashRouteMatch ? hashRouteMatch[1] : sanitizedUrl.split('#')[0];
    if (!normalizedUrl) {
      return '/';
    }

    return normalizedUrl.length > 1 && normalizedUrl.endsWith('/')
      ? normalizedUrl.slice(0, -1)
      : normalizedUrl;
  }

  private normalizeHtmlFilePath(htmlFile: string): string {
    const normalizedHtmlFile = (htmlFile || '').trim();
    return normalizedHtmlFile.startsWith('src/')
      ? normalizedHtmlFile.slice(4)
      : normalizedHtmlFile;
  }

  private addFilesToComposer(files: File[]): void {
    if (!files.length) {
      return;
    }

    const nextAttachments = files.map((file) => this.createComposerAttachment(file));
    this.draftAttachments = [...this.draftAttachments, ...nextAttachments];
    this.view = ViewMode.CHAT;
    this.focusInput();
  }

  private createComposerAttachment(file: File): ComposerAttachment {
    const normalizedType = file.type || 'application/octet-stream';
    const isImage = normalizedType.startsWith('image/');
    const exceedsLimit = file.size > AiAgentComponent.ATTACHMENT_MAX_SIZE_BYTES;

    return {
      id: `composer-attachment-${++this.composerAttachmentSequence}`,
      file,
      fileName: file.name || this.translate.instant('ai.agent.attachments.untitled'),
      contentType: normalizedType,
      size: file.size,
      kind: isImage ? AgentAttachmentKind.IMAGE : AgentAttachmentKind.DOCUMENT,
      state: exceedsLimit ? 'rejected' : 'pending',
      failureCode: exceedsLimit ? 'file_too_large' : undefined,
      message: exceedsLimit
        ? this.translate.instant('ai.agent.attachments.validation.file_too_large', {limit: '2 MB'})
        : undefined
    };
  }

  private mapComposerAttachmentToResult(attachment: ComposerAttachment, uiState: AgentAttachmentUiState): AgentAttachmentResult {
    return {
      localId: attachment.id,
      fileName: attachment.fileName,
      contentType: attachment.contentType,
      size: attachment.size,
      kind: attachment.kind,
      status: uiState === 'success' ? AgentAttachmentStatus.INCORPORATED : undefined,
      includedInContext: uiState === 'success' ? true : undefined,
      failureCode: attachment.failureCode,
      message: attachment.message,
      uiState
    };
  }

  private getSendableAttachments(): ComposerAttachment[] {
    return this.draftAttachments.filter((attachment) => attachment.state === 'pending');
  }

  private consumeSubmittedAttachments(attachmentIds: string[]): void {
    if (!attachmentIds.length) {
      return;
    }

    const ids = new Set(attachmentIds);
    this.draftAttachments = this.draftAttachments.filter((attachment) => !ids.has(attachment.id));
  }

  private resetComposerQuestion(): void {
    this.question = '';
    if (this.aiInput?.nativeElement) {
      this.aiInput.nativeElement.value = '';
      this.aiInput.nativeElement.style.height = 'auto';
    }
  }

  private restoreFailedDraft(): void {
    if (!this.pendingDraftRestore) {
      return;
    }

    this.question = this.pendingDraftRestore.question;
    this.draftAttachments = [...this.pendingDraftRestore.attachments, ...this.draftAttachments];
    this.pendingDraftRestore = undefined;

    setTimeout(() => {
      this.focusInput();
      this.adjustTextareaHeight();
    });
  }

  private getImageExtension(contentType: string): string {
    const mimeSegment = (contentType || '').split('/')[1] || 'png';
    return mimeSegment.replace(/[^a-z0-9]/gi, '') || 'png';
  }

  private scrollToBottom(): void {
    const el = this.scrollContainer?.nativeElement;
    el?.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }

  private isNearBottom(threshold = 80): boolean {
    const el = this.scrollContainer?.nativeElement;
    if (!el) {
      return true;
    }

    return el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
  }
}
